import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Music,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  ListMusic,
  Heart,
  ArrowRight,
} from 'lucide-react';
import { useAudioPlayerContext } from './music/AudioPlayerProvider';

const blobOneMotion = {
  x: [0, 20, 50, 20, 70, 50, 0],
  y: [0, 20, 50, 30, 5, 40, 0],
};

const blobTwoMotion = {
  x: [0, -50, -70, -60, 0, -30, 0],
  y: [0, -40, -30, -10, -55, -20, 0],
};

export default function GlassMusicPlayer({ music = [] }) {
  const player = useAudioPlayerContext();
  const [isLiked, setIsLiked] = useState(false);

  const currentTrack = player.currentTrack;
  const isPlaying = player.isPlaying;

  const togglePlay = () => {
    if (music.length === 0) return;
    if (!currentTrack) {
      player.handlePlayPause(music[0], music);
    } else {
      player.togglePlayPause();
    }
  };

  const trackName = currentTrack?.name || 'A New Song';
  const trackArtist = currentTrack?.artist || 'The Asidors';

  return (
    <div className="relative h-[254px] w-[190px]" aria-label="Music player">
      {/* Animated glow blobs */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 56,
          height: 56,
          top: 30,
          left: 20,
          backgroundColor: 'rgb(131, 25, 163)',
          filter: 'drop-shadow(0 0 10px rgb(131, 25, 163))',
        }}
        animate={blobOneMotion}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 56,
          height: 56,
          top: 90,
          left: 90,
          backgroundColor: 'rgb(29, 209, 149)',
          filter: 'drop-shadow(0 0 10px rgb(29, 209, 149))',
        }}
        animate={blobTwoMotion}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Glass card */}
      <div
        className="absolute inset-0 z-10 flex flex-col items-center rounded-[10px] border border-white/20"
        style={{
          background: 'rgba(255, 255, 255, 0.55)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          backdropFilter: 'blur(8.5px)',
          WebkitBackdropFilter: 'blur(8.5px)',
        }}
      >
        {/* Title pill */}
        <span className="mt-3 rounded-full border border-black/10 px-2.5 py-0.5 text-[10px] font-medium text-gray-500">
          Music
        </span>

        {/* Music icon box */}
        <div className="mt-5 flex h-20 w-20 items-center justify-center rounded-[15px] bg-white/70">
          <Music size={28} className="text-gray-500" />
        </div>

        {/* Track info */}
        <div className="mt-2.5 w-36 truncate text-center text-[12px] font-medium text-gray-600">
          {trackName}
        </div>
        <div className="mt-0.5 w-28 truncate text-center text-[10px] font-medium text-gray-500">
          {trackArtist}
        </div>

        {/* Main controls */}
        <div className="mt-3 flex w-24 items-center justify-between">
          <button
            onClick={player.playPrev}
            className="text-gray-600 transition hover:text-gray-900"
            aria-label="Previous track"
          >
            <SkipBack size={14} fill="currentColor" />
          </button>
          <button
            onClick={togglePlay}
            className="text-gray-800 transition hover:text-gray-900"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
          </button>
          <button
            onClick={player.playNext}
            className="text-gray-600 transition hover:text-gray-900"
            aria-label="Next track"
          >
            <SkipForward size={14} fill="currentColor" />
          </button>
        </div>

        {/* Secondary controls */}
        <div className="mt-4 flex w-full items-center justify-between px-6">
          <button
            onClick={player.toggleShuffle}
            className="text-gray-800 transition hover:text-gray-950"
            style={{ color: player.isShuffling ? 'rgb(34, 211, 238)' : 'rgba(29, 28, 28, 0.829)' }}
            aria-label="Shuffle"
          >
            <Shuffle size={14} />
          </button>
          <button className="text-gray-800 transition hover:text-gray-950" aria-label="Playlist">
            <ListMusic size={14} />
          </button>
          <button
            onClick={() => setIsLiked((v) => !v)}
            className="transition hover:text-gray-950"
            style={{ color: isLiked ? 'rgb(225, 29, 72)' : 'rgba(29, 28, 28, 0.829)' }}
            aria-label="Like"
          >
            <Heart size={14} fill={isLiked ? 'currentColor' : 'none'} />
          </button>
          <a href="/music" className="text-gray-800 transition hover:text-gray-950" aria-label="View all music">
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
}
